/**
 * sync.ts — Dexie outbox ↔ Supabase 同步
 */
import { db, type Expense, type OdometerReading, type ReminderRule } from './db'
import { supabase } from './supabase'

let _isSyncing = false

/** flush outbox：把待上傳的操作依序送到 Supabase */
export async function flushOutbox(): Promise<void> {
  if (_isSyncing) return
  _isSyncing = true
  try {
    const items = await db.outbox.orderBy('id').toArray()
    for (const item of items) {
      const payload = JSON.parse(item.payload)
      if (item.operation === 'upsert') {
        const { error } = await supabase.from(item.table).upsert(payload)
        if (error) throw error
      } else {
        // soft delete：直接 upsert（deleted_at 已在 payload 內）
        const { error } = await supabase.from(item.table).upsert(payload)
        if (error) throw error
      }
      await db.outbox.delete(item.id!)
    }
  } finally {
    _isSyncing = false
  }
}

/** 寫 expense（先存 Dexie，再進 outbox） */
export async function saveExpense(expense: Expense): Promise<void> {
  const now = new Date().toISOString()
  const record: Expense = { ...expense, updated_at: now }
  await db.expenses.put(record)
  await db.outbox.add({
    table: 'expenses',
    record_id: record.id,
    operation: 'upsert',
    payload: JSON.stringify(record),
    created_at: now,
  })
  flushOutbox().catch(() => {/* 離線時靜默失敗，等連線再 flush */})
}

/** 軟刪除 expense */
export async function deleteExpense(id: string): Promise<void> {
  const now = new Date().toISOString()
  await db.expenses.update(id, { deleted_at: now, updated_at: now })
  const record = await db.expenses.get(id)
  if (!record) return
  await db.outbox.add({
    table: 'expenses',
    record_id: id,
    operation: 'delete',
    payload: JSON.stringify(record),
    created_at: now,
  })
  flushOutbox().catch(() => {})
}

/** 寫 odometer_reading */
export async function saveOdometer(reading: OdometerReading): Promise<void> {
  const now = new Date().toISOString()
  await db.odometer_readings.put(reading)
  await db.outbox.add({
    table: 'odometer_readings',
    record_id: reading.id,
    operation: 'upsert',
    payload: JSON.stringify(reading),
    created_at: now,
  })
  flushOutbox().catch(() => {})
}

/** 從 Supabase 拉全量資料（首次登入或強制重新整理） */
export async function pullAll(): Promise<void> {
  const { data: expenses, error: e1 } = await supabase
    .from('expenses').select('*')
  if (e1) throw e1
  if (expenses?.length) await db.expenses.bulkPut(expenses as Expense[])

  const { data: odometers, error: e2 } = await supabase
    .from('odometer_readings').select('*')
  if (e2) throw e2
  if (odometers?.length) await db.odometer_readings.bulkPut(odometers as OdometerReading[])

  const { data: prices, error: e3 } = await supabase
    .from('fuel_prices').select('*')
  if (e3) throw e3
  if (prices?.length) await db.fuel_prices.bulkPut(prices as any[])
}

/** 取目前里程（本機最大值） */
export async function getCurrentOdometer(): Promise<number> {
  const [expMax, odoMax] = await Promise.all([
    db.expenses
      .filter(e => e.deleted_at === null && e.odometer_km !== null)
      .toArray()
      .then(arr => Math.max(0, ...arr.map(e => e.odometer_km ?? 0))),
    db.odometer_readings
      .filter(r => (r as any).deleted_at === null || (r as any).deleted_at === undefined)
      .toArray()
      .then(arr => Math.max(0, ...arr.map(r => r.odometer_km))),
  ])
  return Math.max(expMax, odoMax)
}

/** 儲存 reminder_rule */
export async function saveReminderRule(rule: ReminderRule): Promise<void> {
  await db.reminder_rules.put(rule)
  const { error } = await supabase.from('reminder_rules').upsert(rule)
  if (error) console.warn('[sync] reminder_rule upsert failed', error)
}

/** 刪除 reminder_rule */
export async function deleteReminderRule(id: string): Promise<void> {
  await db.reminder_rules.delete(id)
  const { error } = await supabase.from('reminder_rules').delete().eq('id', id)
  if (error) console.warn('[sync] reminder_rule delete failed', error)
}

/** 取指定日期最近的油價（本機） */
export async function getFuelPrice(date: string, grade = '95'): Promise<number | null> {
  const all = await db.fuel_prices
    .where('effective_date').belowOrEqual(date)
    .filter(p => p.grade === grade)
    .reverse()
    .first()
  return all?.price_per_liter ?? null
}
